/**
 * custom-cart-attributes.js
 * Syncs cart note-attributes to Shopify so they appear in admin
 * "Additional details" on the orders that need them.
 *
 * When the cart contains preorder / partial-backorder items → sets
 * preorder + preorder_dispatch_estimate (clears legacy preorder_items).
 *
 * A cart with nothing delayed writes NO attributes at all — only keys the cart
 * already carries a stale value for get cleared, so ordinary in-stock orders
 * land in admin with an empty "Additional details" panel. `preorder: true` is
 * reserved for genuine preorder / backorder lines; a made-to-order
 * (print-on-demand) line reports its lead time in preorder_dispatch_estimate
 * without claiming the order is a preorder.
 *
 * Data source: <script type="application/json" data-ccc-cart-state>
 * from cart-products.liquid.
 */

import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { getAdminDispatchEstimate, resolveDispatchLookupKey } from '@theme/custom-dispatch-manager';

/**
 * @param {Record<string, string>} attributes
 */
async function updateCartAttributes(attributes) {
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes }),
  });
  if (!res.ok) throw new Error(`/cart/update.js ${res.status}`);
  return res.json();
}

/**
 * @returns {{ hasPreorders: boolean, preorderItems: Array<object> } | null}
 */
function readCartState() {
  const el = document.querySelector('script[data-ccc-cart-state]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[Phaser] Failed to parse cart state JSON', e);
    return null;
  }
}

/**
 * @returns {Record<string, string> | null}
 */
function readCurrentCartAttributes() {
  const el = document.querySelector('script[data-ccc-cart-attrs]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[Phaser] Failed to parse current cart attributes JSON', e);
    return null;
  }
}

/**
 * Every attribute this module owns. A key absent from the desired set is only
 * sent (as '') when the cart still carries a stale value for it — Shopify drops
 * an attribute whose value is an empty string.
 *
 * `shipping_estimate` is a legacy CCC key, kept here purely so old carts get
 * cleaned up. `locksmith` is the same:
 * a leftover from the uninstalled Locksmith app, blanked so it stops showing in
 * admin "Additional details".
 */
const OWNED_ATTRIBUTE_KEYS = [
  'preorder',
  'preorder_items',
  'preorder_dispatch_estimate',
  '_eta_snapshot',
  'shipping_estimate',
  'locksmith',
];

/** Kinds that mean the customer is genuinely waiting on stock. */
const TRUE_DELAY_KINDS = new Set(['preorder', 'partial_backorder']);

/**
 * Attributes this cart should end up with. Keys are omitted — not blanked —
 * when there is nothing to say, so an all-in-stock cart yields {}.
 *
 * @param {{ hasPreorders?: boolean, preorderItems?: Array<object> }} state
 * @returns {Record<string, string>}
 */
function buildDesiredAttributes(state) {
  const items = state.hasPreorders && state.preorderItems?.length ? state.preorderItems : [];
  if (items.length === 0) return {};

  const dispatchParts = [];
  for (const item of items) {
    const name = item.name || 'Item';
    const lookupKey = resolveDispatchLookupKey(item.type, item.vendor);
    const estimatedRestockDate = item.estimatedRestockDate || null;
    if (!lookupKey && !estimatedRestockDate) continue;

    const estimate = getAdminDispatchEstimate(lookupKey, { estimatedRestockDate });
    if (item.kind === 'partial_backorder') {
      const onHand = Number(item.onHand) || 0;
      const backordered = Number(item.backordered) || 0;
      dispatchParts.push(`${name}: ${onHand} in stock + ${backordered} backorder (${estimate})`);
    } else {
      dispatchParts.push(`${name}: ${estimate}`);
    }
  }

  // No resolvable estimate for any line → nothing worth stamping on the order.
  if (dispatchParts.length === 0) return {};

  /** @type {Record<string, string>} */
  const desired = { preorder_dispatch_estimate: dispatchParts.join(' | ') };

  const hasTrueDelay = items.some((item) => TRUE_DELAY_KINDS.has(item.kind));
  if (!hasTrueDelay) return desired;

  desired.preorder = 'true';

  // Structured snapshot (hidden `_eta_snapshot` attribute) — machine-readable
  // mirror read by the phaser-order-insight customer account extension for
  // exact date-moved / arrived comparisons on the order page. Underscore
  // prefix keeps it off customer-facing surfaces. Only written alongside a
  // real delay; a made-to-order line has no ETA to move.
  desired._eta_snapshot = JSON.stringify({
    roast: null,
    lines: items.map((item) => ({
      variantId: item.variantId || null,
      name: item.name || null,
      eta: item.estimatedRestockDate || null,
      kind: item.kind || 'preorder',
    })),
  });

  return desired;
}

let _lastSyncedJSON = '';

async function syncCartAttributes() {
  try {
    const state = readCartState();
    if (!state) return;

    const desired = buildDesiredAttributes(state);
    const json = JSON.stringify(desired);
    if (json === _lastSyncedJSON) return;

    const current = readCurrentCartAttributes();

    // Nothing to write and no island to diff against — leave the cart alone
    // rather than posting a wall of blanks.
    if (!current && Object.keys(desired).length === 0) {
      _lastSyncedJSON = json;
      return;
    }

    /** @type {Record<string, string>} */
    const payload = {};

    for (const key of OWNED_ATTRIBUTE_KEYS) {
      const want = desired[key] || '';
      // No server-rendered island (unexpected): fall back to sending the full
      // owned set so a stale value can never survive to checkout.
      const have = current ? current[key] || '' : null;
      if (have === null || want !== have) payload[key] = want;
    }

    if (Object.keys(payload).length === 0) {
      _lastSyncedJSON = json;
      return;
    }

    await updateCartAttributes(payload);
    _lastSyncedJSON = json;
  } catch (err) {
    console.warn('[Phaser] Cart attributes sync failed:', err);
  }
}

setTimeout(syncCartAttributes, 150);

// Horizon 4.2.0: the cart section is re-rendered on a browser Back without a
// cartLinesUpdate; the event fires after that morph has finished.
document.addEventListener(ThemeEvents.cartSectionRestored, () => syncCartAttributes());

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(() => syncCartAttributes())
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[Phaser] Cart attributes sync skipped:', error);
      }
    });
});
